import { API_PATH, BASE_PATH } from './config';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Card, CardContent, CardActionArea,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Select, MenuItem, FormControl, InputLabel,
    Chip, IconButton, CircularProgress, Avatar, AvatarGroup,
    Divider, Tooltip, Snackbar, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { getTripTitle } from './utils/tripTitle';

const API = `${API_PATH}/expense_reports.php`;

const CURRENCIES = ['USD','EUR','GBP','JPY','AUD','CAD','CHF','ILS','THB','SGD','INR','MXN','BRL','AED','HKD'];

function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    return `hsl(${h},55%,50%)`;
}

function MemberAvatars({ members, max = 4 }) {
    return (
        <AvatarGroup max={max} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 13 } }}>
            {members.map(m => (
                <Tooltip key={m.pu_id} title={`${m.display_name} (${m.role})`}>
                    <Avatar sx={{ bgcolor: stringToColor(m.display_name) }}>
                        {m.display_name[0].toUpperCase()}
                    </Avatar>
                </Tooltip>
            ))}
        </AvatarGroup>
    );
}

function ReportCard({ report, isShared, onEdit, onDelete, onAssign, onManageMembers, onClick, defaultCurrency, exchangeRates }) {
    let totalStr = '—';
    if (report.totals && report.totals.length > 0) {
        if (exchangeRates && defaultCurrency) {
            const toRate = exchangeRates[defaultCurrency] || 1;
            const usdTotal = report.totals.reduce((sum, t) => {
                const fromRate = exchangeRates[t.currency] || 1;
                return sum + parseFloat(t.total) / fromRate;
            }, 0);
            const converted = usdTotal * toRate;
            totalStr = `${defaultCurrency} ${converted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        } else {
            totalStr = report.totals.map(t => `${t.currency} ${parseFloat(t.total).toLocaleString(undefined, { maximumFractionDigits: 2 })}`).join(' + ');
        }
    }

    return (
        <Card sx={{
            minWidth: 280,
            flexGrow: 1,
            maxWidth: 400,
            transition: 'all 0.2s',
            '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 },
        }}>
            <CardActionArea onClick={onClick} sx={{ pb: 0 }}>
                <Box sx={{
                    background: isShared
                        ? 'linear-gradient(135deg,#11998e,#38ef7d)'
                        : 'linear-gradient(135deg,#f093fb,#f5576c)',
                    p: 2, display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 2, p: 1, display: 'flex' }}>
                        <AttachMoneyIcon sx={{ color: '#fff', fontSize: 26 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {report.title}
                        </Typography>
                        {isShared && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                Role: {report.my_role}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <CardContent sx={{ pt: 1.5, pb: '8px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <FlightTakeoffIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            {report.trip_name || 'Unassigned'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} color="primary">
                            {totalStr}
                        </Typography>
                        {report.members && report.members.length > 0 && (
                            <MemberAvatars members={report.members} />
                        )}
                    </Box>
                </CardContent>
            </CardActionArea>
            {!isShared && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, px: 1, pb: 1 }}>
                    <Tooltip title="Edit title">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(report); }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={report.trip_name ? 'Reassign / Detach trip' : 'Assign to trip'}>
                        <IconButton size="small" onClick={e => { e.stopPropagation(); onAssign(report); }}>
                            {report.trip_name ? <LinkOffIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage members">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); onManageMembers(report); }}>
                            <PersonAddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete report">
                        <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(report); }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Card>
    );
}

export default function ExpenseReportsPage({ user }) {
    const navigate = useNavigate();
    const userId = user?.pu_id || null;
    const userParam = userId ? `&user_id=${userId}` : '';

    const [owned, setOwned]   = useState([]);
    const [shared, setShared] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assignError, setAssignError] = useState('');

    // Create dialog
    const [showCreate, setShowCreate] = useState(false);
    const [newTitle, setNewTitle]     = useState('');
    const [creating, setCreating]     = useState(false);

    // Edit dialog
    const [editReport, setEditReport]   = useState(null);
    const [editTitle, setEditTitle]     = useState('');

    // Assign dialog
    const [assignReport, setAssignReport] = useState(null);
    const [availableTrips, setAvailableTrips] = useState([]);
    const [selectedTripId, setSelectedTripId] = useState(null);
    const [assigning, setAssigning] = useState(false);

    // Members dialog
    const [membersReport, setMembersReport] = useState(null);
    const [memberSearchId, setMemberSearchId] = useState('');
    const [memberRole, setMemberRole] = useState('view');

    // Delete confirm
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [exchangeRates, setExchangeRates] = useState(null);

    useEffect(() => {
        fetch(`${API_PATH}/exchange_rates.php`)
            .then(r => r.json())
            .then(data => { if (data.result === 'success') setExchangeRates(data.rates); })
            .catch(() => {});
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}?action=list${userParam}&t=${Date.now()}`);
            const data = await res.json();
            setOwned(data.owned || []);
            setShared(data.shared || []);
        } finally {
            setLoading(false);
        }
    }, [userParam]);

    useEffect(() => { load(); }, [load]);

    // ── Create ──────────────────────────────────────
    const handleCreate = async () => {
        if (!newTitle.trim()) return;
        setCreating(true);
        await fetch(`${API}?action=create${userParam}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: newTitle.trim() }),
        });
        setCreating(false);
        setShowCreate(false);
        setNewTitle('');
        load();
    };

    // ── Edit ────────────────────────────────────────
    const handleEditSave = async () => {
        await fetch(`${API}?action=update${userParam}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: editReport.id, title: editTitle.trim() }),
        });
        setEditReport(null);
        load();
    };

    // ── Assign trip ─────────────────────────────────
    const handleOpenAssign = async (report) => {
        setAssignReport(report);
        setSelectedTripId(report.trip_id || null);
        const res = await fetch(`${API}?action=trips${userParam}`);
        const data = await res.json();
        setAvailableTrips(data.trips || []);
    };

    const handleAssign = async () => {
        setAssigning(true);
        try {
            const res = await fetch(`${API}?action=assign_trip${userParam}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: assignReport.id, trip_id: selectedTripId, user_id: userId }),
            });
            const result = await res.json();
            if (!result.success) {
                setAssignError(result.error || 'Failed to assign trip');
            }
        } catch (e) {
            setAssignError('Network error');
        }
        setAssigning(false);
        setAssignReport(null);
        await load();
    };

    // ── Members ─────────────────────────────────────
    const handleAddMember = async () => {
        const pu_id = parseInt(memberSearchId);
        if (!pu_id) return;
        await fetch(`${API}?action=add_member${userParam}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ report_id: membersReport.id, member_pu_id: pu_id, role: memberRole }),
        });
        setMemberSearchId('');
        load();
        // Refresh membersReport from updated list
        const res = await fetch(`${API}?action=list${userParam}`);
        const data = await res.json();
        const updated = [...(data.owned||[]), ...(data.shared||[])].find(r => r.id === membersReport.id);
        if (updated) setMembersReport(updated);
        setOwned(data.owned || []);
        setShared(data.shared || []);
    };

    const handleChangeRole = async (member_pu_id, role) => {
        await fetch(`${API}?action=update_member_role${userParam}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ report_id: membersReport.id, member_pu_id, role }),
        });
        load();
    };

    const handleRemoveMember = async (member_pu_id) => {
        await fetch(`${API}?action=remove_member${userParam}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ report_id: membersReport.id, member_pu_id }),
        });
        setMembersReport(null);
        load();
    };

    // ── Delete ──────────────────────────────────────
    const handleDelete = async () => {
        await fetch(`${API}?action=delete&id=${deleteTarget.id}${userParam}`, { method: 'DELETE' });
        setDeleteTarget(null);
        load();
    };

    // ── Navigate into report ─────────────────────────
    const openReport = (report) => {
        navigate(`/expenses/${report.id}`);
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight={700}>My Expenses</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreate(true)} sx={{ borderRadius: 3 }}>
                    New Report
                </Button>
            </Box>

            {/* Owned reports */}
            <Typography variant="h6" sx={{ mb: 1.5, color: 'text.secondary' }}>My Reports</Typography>
            {owned.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    No expense reports yet. Create one to get started.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                    {owned.map(r => (
                        <ReportCard
                            key={r.id}
                            report={r}
                            isShared={false}
                            onClick={() => openReport(r)}
                            onEdit={rep => { setEditReport(rep); setEditTitle(rep.title); }}
                            onDelete={rep => setDeleteTarget(rep)}
                            onAssign={handleOpenAssign}
                            onManageMembers={rep => setMembersReport(rep)}
                            defaultCurrency={user?.currency || 'USD'}
                            exchangeRates={exchangeRates}
                        />
                    ))}
                </Box>
            )}

            {/* Shared with me */}
            {shared.length > 0 && (
                <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1.5, color: 'text.secondary' }}>Shared With Me</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {shared.map(r => (
                            <ReportCard
                                key={r.id}
                                report={r}
                                isShared={true}
                                onClick={() => openReport(r)}
                                onEdit={() => {}}
                                onDelete={() => {}}
                                onAssign={() => {}}
                                onManageMembers={() => {}}
                                defaultCurrency={user?.currency || 'USD'}
                                exchangeRates={exchangeRates}
                            />
                        ))}
                    </Box>
                </>
            )}

            {/* ── Create dialog ── */}
            <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="xs" fullWidth>
                <DialogTitle>New Expense Report</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField label="Report Name" value={newTitle} onChange={e => setNewTitle(e.target.value)}
                        placeholder='e.g. "Chiang Mai Gas & Food"' fullWidth autoFocus />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCreate(false)}>Cancel</Button>
                    <Button variant="contained" disabled={!newTitle.trim() || creating} onClick={handleCreate}>
                        {creating ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit dialog ── */}
            <Dialog open={!!editReport} onClose={() => setEditReport(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Report</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField label="Report Name" value={editTitle} onChange={e => setEditTitle(e.target.value)} fullWidth autoFocus />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditReport(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!editTitle.trim()} onClick={handleEditSave}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* ── Assign trip dialog ── */}
            <Dialog open={!!assignReport} onClose={() => setAssignReport(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{assignReport?.trip_name ? 'Reassign / Detach Trip' : 'Assign to Trip'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        All expenses in this report will move with it. Trip members will automatically get edit access.
                    </Typography>
                    {/* Detach option */}
                    <Box
                        onClick={() => setSelectedTripId(null)}
                        sx={{
                            p: 1.5, mb: 1, border: '1px solid', borderRadius: 1, cursor: 'pointer',
                            borderColor: selectedTripId === null ? 'primary.main' : 'divider',
                            bgcolor: selectedTripId === null ? 'primary.light' : 'transparent',
                        }}
                    >
                        <Typography variant="body2" fontWeight={selectedTripId === null ? 700 : 400}>
                            No trip (unassigned)
                        </Typography>
                    </Box>
                    {availableTrips.map(trip => (
                        <Box
                            key={trip.trip_id}
                            onClick={() => setSelectedTripId(trip.trip_id)}
                            sx={{
                                p: 1.5, mb: 1, border: '1px solid', borderRadius: 1, cursor: 'pointer',
                                borderColor: selectedTripId === trip.trip_id ? 'primary.main' : 'divider',
                                bgcolor: selectedTripId === trip.trip_id ? 'primary.light' : 'transparent',
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={selectedTripId === trip.trip_id ? 700 : 400}>
                                {getTripTitle(trip)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {trip.start_location}{trip.start_date ? ` · ${trip.start_date}` : ''}
                            </Typography>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignReport(null)}>Cancel</Button>
                    <Button variant="contained" disabled={assigning} onClick={handleAssign}>
                        {assigning ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Members dialog ── */}
            <Dialog open={!!membersReport} onClose={() => setMembersReport(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Manage Members — {membersReport?.title}</DialogTitle>
                <DialogContent>
                    {/* Existing members */}
                    {(membersReport?.members || []).filter(m => m.role !== 'owner').map(m => (
                        <Box key={m.pu_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: stringToColor(m.display_name), width: 32, height: 32, fontSize: 14 }}>
                                {m.display_name[0].toUpperCase()}
                            </Avatar>
                            <Typography sx={{ flex: 1 }}>{m.display_name}</Typography>
                            <Select
                                size="small"
                                value={m.role}
                                onChange={e => handleChangeRole(m.pu_id, e.target.value)}
                                sx={{ width: 120 }}
                            >
                                <MenuItem value="edit">Edit</MenuItem>
                                <MenuItem value="view">View Only</MenuItem>
                            </Select>
                            <IconButton size="small" color="error" onClick={() => handleRemoveMember(m.pu_id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Add member by User ID</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            label="User ID (pu_id)"
                            value={memberSearchId}
                            onChange={e => setMemberSearchId(e.target.value)}
                            sx={{ flex: 1 }}
                        />
                        <Select size="small" value={memberRole} onChange={e => setMemberRole(e.target.value)} sx={{ width: 130 }}>
                            <MenuItem value="edit">Edit</MenuItem>
                            <MenuItem value="view">View Only</MenuItem>
                        </Select>
                        <Button variant="contained" onClick={handleAddMember} disabled={!memberSearchId}>Add</Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMembersReport(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete confirm ── */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Report?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Delete <strong>"{deleteTarget?.title}"</strong>? Expenses inside will not be deleted — they'll become unassigned.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Error snackbar */}
            <Snackbar open={!!assignError} autoHideDuration={5000} onClose={() => setAssignError('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setAssignError('')}>{assignError}</Alert>
            </Snackbar>
        </Box>
    );
}
